import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import MathRenderer from './MathRenderer';
import jsPDF from 'jspdf';
import { RefreshCw, Trash2, Edit2, Check, X, Plus, Wand2, Hand, ChevronDown, ChevronUp, Download, FileText } from 'lucide-react';

/**
 * Reusable Test/Worksheet Generator with Manual + Auto modes.
 * Props:
 *   classLevel: number (default 6)
 *   onSave: (questions, title, totalMarks) => void  — called when user saves/creates the worksheet
 *   showTitle: boolean (default true) — show title input
 *   mode: 'teacher' | 'student' | 'parent'
 */
export default function TestGenerator({ classLevel = 6, onSave, showTitle = true, mode = 'student' }) {
  const { API, getHeaders } = useAuth();
  const [config, setConfig] = useState(null);
  const [genMode, setGenMode] = useState('auto'); // 'auto' | 'manual'
  const [title, setTitle] = useState('');
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingIdx, setEditingIdx] = useState(-1);
  const [editText, setEditText] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // Filters (shared for both modes)
  const [filters, setFilters] = useState({
    class_level: classLevel, chapters: [], microconcepts: [], difficulties: [], tags: [],
  });
  // Auto mode: question type counts
  const [typeCounts, setTypeCounts] = useState({
    mcq: 10, fill_blank: 3, true_false: 2, integer_type: 2, subjective: 0,
  });
  // Manual mode: browsed questions
  const [browseResults, setBrowseResults] = useState([]);
  const [browseType, setBrowseType] = useState('');
  const [browsing, setBrowsing] = useState(false);

  useEffect(() => {
    fetch(`${API}/config/all`).then(r => r.json()).then(setConfig).catch(() => {});
  }, [API]);

  useEffect(() => {
    setFilters(f => ({ ...f, class_level: classLevel }));
  }, [classLevel]);

  const chapters = config?.config_chapters?.filter(c => c.class_key === String(filters.class_level)) || [];
  const microconcepts = config?.config_microconcepts?.filter(c => c.class_key === String(filters.class_level)) || [];
  const difficulties = config?.config_difficulty_levels || [];
  const qTypes = config?.config_question_types || [];

  const toggleArray = (arr, val) => arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];

  // ── Auto Generate ──
  const autoGenerate = async () => {
    const total = Object.values(typeCounts).reduce((a, b) => a + (parseInt(b) || 0), 0);
    if (total === 0) { toast.error('Set at least 1 question type count'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/practice/generate-paper`, {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          class_level: filters.class_level,
          chapters: filters.chapters,
          microconcepts: filters.microconcepts,
          difficulties: filters.difficulties,
          tags: filters.tags,
          type_counts: Object.fromEntries(Object.entries(typeCounts).filter(([, v]) => parseInt(v) > 0).map(([k, v]) => [k, parseInt(v)])),
          exclude_ids: questions.map(q => q.id),
        }),
      });
      const data = await res.json();
      if (data.questions?.length) {
        setQuestions(prev => [...prev, ...data.questions]);
        toast.success(`Added ${data.questions.length} questions (${data.total_marks} marks)`);
      } else {
        toast.error('No questions found for these criteria');
      }
    } catch { toast.error('Generation failed'); }
    finally { setLoading(false); }
  };

  // ── Manual Browse ──
  const browseQuestions = async () => {
    setBrowsing(true);
    try {
      const params = new URLSearchParams({ class_level: filters.class_level, limit: '50' });
      if (filters.chapters.length) params.set('chapter', filters.chapters[0]);
      if (filters.difficulties.length) params.set('difficulty', filters.difficulties[0]);
      if (browseType) params.set('question_type', browseType);
      const res = await fetch(`${API}/practice/deep/questions?${params}`, { headers: getHeaders() });
      const data = await res.json();
      setBrowseResults(data.questions || []);
    } catch { toast.error('Browse failed'); }
    finally { setBrowsing(false); }
  };

  const addFromBrowse = (q) => {
    if (questions.find(x => x.id === q.id)) { toast.info('Already added'); return; }
    setQuestions(prev => [...prev, q]);
    toast.success('Added!');
  };

  // ── Question actions ──
  const removeQuestion = (idx) => {
    setQuestions(prev => prev.filter((_, i) => i !== idx));
  };

  const replaceQuestion = async (idx) => {
    const q = questions[idx];
    try {
      const res = await fetch(`${API}/practice/replace-question`, {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_id: q.id,
          class_level: q.class_level,
          chapter: q.chapter,
          difficulty: q.difficulty,
          question_type: q.question_type,
          exclude_ids: questions.map(x => x.id),
        }),
      });
      const data = await res.json();
      if (data.question) {
        setQuestions(prev => prev.map((x, i) => i === idx ? data.question : x));
        toast.success('Replaced!');
      }
    } catch { toast.error('No replacement found'); }
  };

  const saveEdit = (idx) => {
    setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, question_text: editText, question_latex: editText } : q));
    setEditingIdx(-1);
    toast.success('Updated!');
  };

  const totalMarks = questions.reduce((a, q) => a + (q.marks || 1), 0);

  const downloadPDF = () => {
    if (!questions.length) return;
    toast.info('Generating PDF...');
    const doc = new jsPDF('p', 'mm', 'a4');
    let y = 20;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(title || 'Test Paper', 105, y, { align: 'center' });
    y += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Questions: ${questions.length} | Total Marks: ${totalMarks} | Class: ${filters.class_level}`, 105, y, { align: 'center' });
    y += 10; doc.line(10, y, 200, y); y += 8;
    questions.forEach((q, i) => {
      if (y > 265) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
      doc.text(`Q${i+1}. [${(q.question_type||'mcq').replace(/_/g,' ')}] [${q.marks||1}m]`, 10, y); y += 6;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
      const lines = doc.splitTextToSize(q.question_text || '', 180);
      doc.text(lines, 14, y); y += lines.length * 5 + 2;
      if (q.option_a) {
        ['A','B','C','D'].forEach(o => { const v = q[`option_${o.toLowerCase()}`]; if (v) { doc.text(`  (${o}) ${v}`, 14, y); y += 5; } });
      }
      y += 4;
    });
    doc.save(`${title || 'test_paper'}.pdf`);
    toast.success('PDF downloaded!');
  };

  const handleSave = () => {
    if (!questions.length) { toast.error('Add at least 1 question'); return; }
    if (onSave) onSave(questions, title || 'Untitled Test', totalMarks);
  };

  return (
    <div className="space-y-4" data-testid="test-generator">
      {/* Mode toggle */}
      <div className="flex gap-2 mb-3">
        <Button data-testid="gen-auto-btn" onClick={() => setGenMode('auto')} variant={genMode === 'auto' ? 'default' : 'outline'}
          className={`flex-1 rounded-full font-bold text-xs ${genMode === 'auto' ? 'bg-[#8338EC] text-white' : ''}`}>
          <Wand2 className="w-3 h-3 mr-1" /> Auto Generate
        </Button>
        <Button data-testid="gen-manual-btn" onClick={() => setGenMode('manual')} variant={genMode === 'manual' ? 'default' : 'outline'}
          className={`flex-1 rounded-full font-bold text-xs ${genMode === 'manual' ? 'bg-[#FF6B35] text-white' : ''}`}>
          <Hand className="w-3 h-3 mr-1" /> Manual Select
        </Button>
      </div>

      {showTitle && (
        <Input data-testid="test-title" value={title} onChange={e => setTitle(e.target.value)}
          placeholder="Test paper title..." className="text-sm font-bold rounded-xl border-2 border-[#E5DACE]" />
      )}

      {/* Filters */}
      <div className="bg-white border-2 rounded-2xl p-3 space-y-2" style={{ borderColor: '#E5DACE' }}>
        <p className="text-xs font-bold text-slate-500">FILTERS</p>
        {/* Chapters */}
        <div>
          <label className="text-[10px] font-bold text-slate-400 block mb-1">Chapters</label>
          <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
            {chapters.map(ch => (
              <button key={ch.key} onClick={() => setFilters(f => ({ ...f, chapters: toggleArray(f.chapters, ch.key) }))}
                className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${filters.chapters.includes(ch.key) ? 'bg-[#FF6B35] text-white border-[#FF6B35]' : 'border-[#E5DACE] text-[#8B7355]'}`}>
                {ch.label}
              </button>
            ))}
          </div>
        </div>
        {/* Microconcepts */}
        {filters.chapters.length > 0 && (
          <div>
            <label className="text-[10px] font-bold text-slate-400 block mb-1">Micro Concepts</label>
            <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
              {microconcepts.filter(mc => !filters.chapters.length || filters.chapters.some(ch => mc.chapter_key === ch || mc.class_key === String(filters.class_level))).slice(0, 20).map(mc => (
                <button key={mc.key} onClick={() => setFilters(f => ({ ...f, microconcepts: toggleArray(f.microconcepts, mc.key) }))}
                  className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${filters.microconcepts.includes(mc.key) ? 'bg-[#8338EC] text-white border-[#8338EC]' : 'border-[#E5DACE] text-[#8B7355]'}`}>
                  {mc.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Difficulty */}
        <div>
          <label className="text-[10px] font-bold text-slate-400 block mb-1">Difficulty</label>
          <div className="flex gap-1">
            {difficulties.map(d => (
              <button key={d.key} onClick={() => setFilters(f => ({ ...f, difficulties: toggleArray(f.difficulties, d.key) }))}
                className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${filters.difficulties.includes(d.key) ? 'bg-[#06D6A0] text-white border-[#06D6A0]' : 'border-[#E5DACE] text-[#8B7355]'}`}>
                {d.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Auto Mode Controls ── */}
      {genMode === 'auto' && (
        <div className="bg-white border-2 rounded-2xl p-3 space-y-2" style={{ borderColor: '#8338EC22' }}>
          <p className="text-xs font-bold text-[#8338EC]">QUESTION TYPE COUNTS</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {qTypes.map(qt => (
              <div key={qt.key} className="flex items-center gap-1">
                <Input type="number" min="0" max="50"
                  value={typeCounts[qt.key] || 0}
                  onChange={e => setTypeCounts(prev => ({ ...prev, [qt.key]: e.target.value }))}
                  className="w-14 text-xs text-center rounded-lg border-[#E5DACE]" />
                <span className="text-[10px] font-semibold text-[#1B4F8A] truncate">{qt.label}</span>
              </div>
            ))}
          </div>
          <Button data-testid="auto-generate-btn" onClick={autoGenerate} disabled={loading}
            className="w-full bg-[#8338EC] text-white font-bold rounded-full text-sm mt-2">
            {loading ? 'Generating...' : `Generate ${Object.values(typeCounts).reduce((a, b) => a + (parseInt(b)||0), 0)} Questions`}
          </Button>
        </div>
      )}

      {/* ── Manual Mode Controls ── */}
      {genMode === 'manual' && (
        <div className="bg-white border-2 rounded-2xl p-3 space-y-2" style={{ borderColor: '#FF6B3522' }}>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-[10px] font-bold text-slate-400 block mb-1">Type</label>
              <select value={browseType} onChange={e => setBrowseType(e.target.value)}
                className="w-full text-xs p-1.5 border rounded-lg border-[#E5DACE]">
                <option value="">All Types</option>
                {qTypes.map(qt => <option key={qt.key} value={qt.key}>{qt.label}</option>)}
              </select>
            </div>
            <Button data-testid="manual-browse-btn" onClick={browseQuestions} disabled={browsing}
              className="bg-[#FF6B35] text-white font-bold rounded-full text-xs px-4">
              {browsing ? 'Loading...' : 'Browse'}
            </Button>
          </div>
          {browseResults.length > 0 && (
            <div className="max-h-48 overflow-y-auto space-y-1 border-t pt-2 mt-2">
              {browseResults.map(q => {
                const isAdded = questions.find(x => x.id === q.id);
                return (
                  <div key={q.id} className={`flex gap-2 items-start p-2 rounded-xl text-xs ${isAdded ? 'bg-green-50' : 'bg-gray-50'}`}>
                    <button onClick={() => addFromBrowse(q)} disabled={isAdded}
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-white shrink-0 ${isAdded ? 'bg-green-400' : 'bg-[#FF6B35]'}`}>
                      {isAdded ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#1B4F8A] truncate">{q.question_text?.slice(0, 80)}</p>
                      <p className="text-[10px] text-slate-400">{q.question_type} · {q.difficulty} · {q.marks || 1}m</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Selected Questions Preview ── */}
      {questions.length > 0 && (
        <div className="bg-white border-2 rounded-2xl p-3" style={{ borderColor: '#E5DACE' }}>
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => setShowPreview(!showPreview)} className="flex items-center gap-1">
              <p className="text-xs font-bold text-[#1B4F8A]">SELECTED QUESTIONS ({questions.length}) · {totalMarks} marks</p>
              {showPreview ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            <div className="flex gap-1">
              <Button data-testid="download-pdf-btn" onClick={downloadPDF} variant="outline" className="rounded-full text-xs px-2 py-1 h-auto">
                <Download className="w-3 h-3 mr-1" /> PDF
              </Button>
              {onSave && (
                <Button data-testid="save-test-btn" onClick={handleSave} className="bg-[#06D6A0] text-white rounded-full text-xs px-3 py-1 h-auto font-bold">
                  <FileText className="w-3 h-3 mr-1" /> Save
                </Button>
              )}
            </div>
          </div>
          {showPreview && (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {questions.map((q, i) => (
                <div key={q.id + i} className="border rounded-xl p-2 relative" style={{ borderColor: '#E5DACE' }} data-testid={`gen-q-${i}`}>
                  <div className="flex items-start gap-2">
                    <span className="text-[10px] font-bold bg-[#1B4F8A] text-white w-5 h-5 rounded-full flex items-center justify-center shrink-0">{i+1}</span>
                    <div className="flex-1 min-w-0">
                      {editingIdx === i ? (
                        <div className="flex gap-1">
                          <Input value={editText} onChange={e => setEditText(e.target.value)} className="text-xs flex-1" />
                          <button onClick={() => saveEdit(i)}><Check className="w-4 h-4 text-green-500" /></button>
                          <button onClick={() => setEditingIdx(-1)}><X className="w-4 h-4 text-red-500" /></button>
                        </div>
                      ) : (
                        <p className="text-xs font-semibold text-[#1B4F8A]">{q.question_text?.slice(0, 120)}{q.question_text?.length > 120 ? '...' : ''}</p>
                      )}
                      <p className="text-[10px] text-slate-400 mt-0.5">{q.question_type} · {q.difficulty} · {q.marks || 1}m · {q.chapter}</p>
                    </div>
                    <div className="flex gap-0.5 shrink-0">
                      <button data-testid={`replace-q-${i}`} onClick={() => replaceQuestion(i)} title="Replace with similar" className="p-1 hover:bg-gray-100 rounded">
                        <RefreshCw className="w-3 h-3 text-[#8338EC]" />
                      </button>
                      <button data-testid={`edit-q-${i}`} onClick={() => { setEditingIdx(i); setEditText(q.question_text || ''); }} title="Edit" className="p-1 hover:bg-gray-100 rounded">
                        <Edit2 className="w-3 h-3 text-[#FF6B35]" />
                      </button>
                      <button data-testid={`delete-q-${i}`} onClick={() => removeQuestion(i)} title="Remove" className="p-1 hover:bg-gray-100 rounded">
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
